/* global femcare, Swiper */

/**
 * A module to initialize and manage all range sliders on the page.
 */
femcare.shared.rangeSlider = {
    sliders: [],
    bazaarvoiceObserver: null,
    selectors: {
        bazaarvoiceSummary: [
            '[data-bv-show="inline_rating"]',
            '[data-bv-show="review-summaries"]',
            'swat-review-summaries-wrapper'
        ].join(', '),
        rangeSlider: '.range-slider',
        scrollbar: '.swiper-scrollbar',
        swiper: '.swiper',
        nextButton: '.range-slider__next',
        prevButton: '.range-slider__previous',
    },

    /**
     * Initializes range sliders and watches for delayed Bazaarvoice review summary rendering.
     */
    init() {
        this.initSlider();
        this.initBazaarvoiceRenderObserver();
    },

    /**
     * Initializes a Swiper instance for each range slider on the page.
     */
    initSlider() {
        const sliders = document.querySelectorAll(this.selectors.rangeSlider);

        sliders.forEach(slider => {
            const swiperElement = slider.querySelector(this.selectors.swiper);

            if (!swiperElement || swiperElement.swiper) {
                return;
            }

            const swiperInstance = this.createSwiper(swiperElement, {
                slider,
                nextButton: slider.querySelector(this.selectors.nextButton),
                prevButton: slider.querySelector(this.selectors.prevButton),
                scrollbar: slider.querySelector(this.selectors.scrollbar),
            });

            this.sliders.push(swiperInstance);
        });
    },

    /**
     * Creates a Swiper instance and enables CSS mode when Bazaarvoice review summaries are present.
     *
     * @param {HTMLElement} swiperElement - The slider element passed to Swiper.
     * @param {object} options - Slider controls and parent element.
     * @param {HTMLElement} options.slider - Parent range slider element.
     * @param {HTMLElement|null} options.nextButton - Next navigation button.
     * @param {HTMLElement|null} options.prevButton - Previous navigation button.
     * @param {HTMLElement|null} options.scrollbar - Swiper scrollbar element.
     * @returns {Swiper} The initialized Swiper instance.
     */
    createSwiper(swiperElement, options) {
        const { slider, nextButton, prevButton, scrollbar } = options;
        const hasBazaarvoiceSummary = this.hasBazaarvoiceSummary(slider);

        if (hasBazaarvoiceSummary) {
            this.markBazaarvoiceSummaryRendered(slider);
        }

        return new Swiper(swiperElement, {
            cssMode: hasBazaarvoiceSummary,
            slidesPerView: 'auto',
            navigation: {
                nextEl: nextButton,
                prevEl: prevButton,
            },
            mousewheel: {
                forceToAxis: true,
            },
            scrollbar: {
                el: scrollbar,
            },
            observer: true,
            observeParents: true,
        });
    },

    /**
     * Checks whether a range slider contains Bazaarvoice rating or review summary markup.
     *
     * @param {HTMLElement} slider - Parent range slider element.
     * @returns {boolean} True when Bazaarvoice summary markup is present.
     */
    hasBazaarvoiceSummary(slider) {
        return Boolean(slider.querySelector(this.selectors.bazaarvoiceSummary));
    },

    /**
     * Marks a range slider after Bazaarvoice review summaries are detected.
     *
     * @param {HTMLElement} slider - Parent range slider element.
     */
    markBazaarvoiceSummaryRendered(slider) {
        slider.setAttribute('data-bv-review-summaries-rendered', 'true');
    },

    /**
     * Observes the document for Bazaarvoice markup rendered after initial Swiper setup.
     */
    initBazaarvoiceRenderObserver() {
        if (
            this.bazaarvoiceObserver ||
            !document.querySelector(this.selectors.rangeSlider) ||
            typeof MutationObserver === 'undefined'
        ) {
            return;
        }

        this.bazaarvoiceObserver = new MutationObserver((mutations) => {
            this.handleBazaarvoiceMutations(mutations);
        });

        this.bazaarvoiceObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-bv-show']
        });

        window.addEventListener('pagehide', () => this.disconnectBazaarvoiceRenderObserver(), { once: true });
    },

    /**
     * Disconnects the Bazaarvoice render observer.
     */
    disconnectBazaarvoiceRenderObserver() {
        if (!this.bazaarvoiceObserver) {
            return;
        }

        this.bazaarvoiceObserver.disconnect();
        this.bazaarvoiceObserver = null;
    },

    /**
     * Processes mutations that may contain newly rendered Bazaarvoice markup.
     *
     * @param {MutationRecord[]} mutations - Mutation records from the Bazaarvoice observer.
     */
    handleBazaarvoiceMutations(mutations) {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes') {
                this.handleBazaarvoiceNode(mutation.target);
                return;
            }

            mutation.addedNodes.forEach((node) => {
                this.handleBazaarvoiceNode(node);
            });
        });
    },

    /**
     * Handles a possible Bazaarvoice node and updates its range slider if needed.
     *
     * @param {Node} node - Mutated or inserted DOM node.
     */
    handleBazaarvoiceNode(node) {
        if (!(node instanceof Element)) {
            return;
        }

        const summary = node.matches(this.selectors.bazaarvoiceSummary)
            ? node
            : node.querySelector(this.selectors.bazaarvoiceSummary);

        if (!summary) {
            return;
        }

        const slider = summary.closest(this.selectors.rangeSlider);

        if (!slider) {
            return;
        }

        this.markBazaarvoiceSummaryRendered(slider);
        this.reinitSliderWithBazaarvoiceMode(slider);
    },

    /**
     * Reinitializes a range slider in CSS mode to avoid Swiper transforms affecting Bazaarvoice tooltips.
     *
     * @param {HTMLElement} slider - Parent range slider element.
     */
    reinitSliderWithBazaarvoiceMode(slider) {
        const swiperElement = slider.querySelector(this.selectors.swiper);

        if (!swiperElement || !swiperElement.swiper || swiperElement.swiper.params.cssMode) {
            return;
        }

        const previousSwiper = swiperElement.swiper;
        const previousIndex = previousSwiper.activeIndex;

        previousSwiper.destroy(true, true);

        const swiperInstance = this.createSwiper(swiperElement, {
            slider,
            nextButton: slider.querySelector(this.selectors.nextButton),
            prevButton: slider.querySelector(this.selectors.prevButton),
            scrollbar: slider.querySelector(this.selectors.scrollbar),
        });

        swiperInstance.slideTo(previousIndex, 0);

        this.sliders = this.sliders.filter(sliderInstance => sliderInstance !== previousSwiper);
        this.sliders.push(swiperInstance);
    },
};
